import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { Category, PostSummary } from '@/types'

import Link from 'next/link'
import { getCategories, getPostCount, getPosts } from '@/lib/journal'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { ContactCta } from '@/components/generals/contact-cta'
import { JournalListing } from '@/components/journal/journal-listing'

interface CategoryPageProps {
    category: Category
    categories: Category[]
    posts: PostSummary[]
    totalCount: number
}

export const getServerSideProps: GetServerSideProps<CategoryPageProps, { slug: string }> = async ({ params }) => {
    const slug = params?.slug

    if (!slug) return { notFound: true }

    const categories = await getCategories()
    const category = categories.find(item => item.slug === slug)

    if (!category) return { notFound: true }

    const [posts, totalCount] = await Promise.all([
        getPosts({ categoryId: category.id }),
        getPostCount()
    ])

    return { props: { category, categories, posts, totalCount } }
}

const CategoryPage: FC<CategoryPageProps> = ({
    category,
    categories,
    posts,
    totalCount
}): ReactNode => {
    return (
        <Layout>
            <Seo
                title={category.name}
                description={category.description ?? `Stories about ${category.name.toLowerCase()} from the journal.`}
            />

            <section id='topic' className='mx-auto max-w-7xl px-5 pt-14 pb-8 sm:px-8 lg:pt-20'>
                <nav aria-label="Breadcrumb">
                    <ol className='flex flex-wrap items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground'>
                        <li>
                            <Link href='/journal' className='transition-colors hover:text-foreground'>
                                Journal
                            </Link>
                        </li>

                        <li aria-hidden='true'>/</li>

                        <li aria-current='page' className='text-primary'>
                            <span data-mdlfy-cms={`categories:${category.id}:name`}>{category.name}</span>
                        </li>
                    </ol>
                </nav>

                <h1
                    data-mdlfy-cms={`categories:${category.id}:name`}
                    className='mt-6 max-w-4xl font-heading text-5xl leading-[1.04] tracking-tight sm:text-6xl lg:text-7xl'
                >
                    {category.name}
                </h1>

                {category.description && (
                    <p
                        data-mdlfy-cms={`categories:${category.id}:description`}
                        className='mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground sm:text-xl'
                    >
                        {category.description}
                    </p>
                )}
            </section>

            <section id='stories' aria-label="Stories" className='mx-auto max-w-7xl px-5 pt-6 sm:px-8'>
                <JournalListing
                    posts={posts}
                    categories={categories}
                    totalCount={totalCount}
                    activeCategoryId={category.id}
                />
            </section>

            <ContactCta />
        </Layout>
    )
}

export default CategoryPage
